import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { VariableSizeList } from "react-window";
import { useMediaQuery, useTheme } from "@mui/material";

const LISTBOX_PADDING = 4;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/* ------------------- Virtualization Helpers ------------------- */
function renderRow(props) {
    const { data, index, style } = props;
    const item = data[index];
    return React.cloneElement(item, {
        style: {
            ...style,
            top: style.top + LISTBOX_PADDING,
            width: "100%",
            boxSizing: "border-box",
            padding: "2px 8px",
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    });
}

const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const StyledUl = React.forwardRef(({ style, ...props }, ref) => (
    <ul
        ref={ref}
        style={{
            ...style,
            width: "100%",
            listStyle: "none",
            padding: 0,
            margin: 0
        }}
        {...props}
    />
));

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"));

    const getChildSize = (child) => {
        if (child?.props?.["data-type"] === "group-header") return 44;
        return 40;
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref} style={{ width: "100%" }}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    height={200}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType={StyledUl}
                    itemData={itemData}
                    itemCount={itemCount}
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

/* ------------------ MAIN COMPONENT ------------------ */

export const GroupedSupplierDropdown = ({
    id,
    key,
    options,
    onChangeItems,
    label,
    selectedItemsData,
    name,
    disabled,
    width,
    height = 40,
    fontSize = 16,
    rowKey
}) => {
    const [selectedItems, setSelectedItems] = useState(selectedItemsData || []);
    const [openModal, setOpenModal] = useState(false);
    const [inputValue, setInputValue] = useState("");
    useEffect(() => {
        if (selectedItemsData) {
            setSelectedItems(selectedItemsData);      // auto-select parent
        }
    }, [selectedItemsData]);

    /* ------------------ Flatten Options: Parent + Children ------------------ */
    const flattened = useMemo(() => {
        try {
            const out = [];

            // ADD GLOBAL SELECT ALL OPTION
            out.push({
                type: "all",
                id: "select_all",
                title: "Select All",
            });
            options.forEach((item, idx) => {
                out.push({
                    type: "parent",
                    id: `parent_${idx}`,
                    parentId: item.parentId,
                    parentName: item.parentName,
                    supplierId: item.supplierId,
                    supplierName: item.supplierName,
                    plants: item.plants,
                    rowKey
                });
            });
            return out;
        } catch (err) {
            return [];
        }
    }, [options]);

    const optionsOfParent = (parentId) =>
        flattened.filter((x) => x.type === "child" && x.parentId === parentId);

    const isChildSelected = (child) =>
        selectedItems.some((s) => s.type === "child" && s.supplierId === child.supplierId);

    const isParentSelected = (supplierId) =>
        selectedItems.some((s) => s.type === "parent" && s.supplierId === supplierId);

    const convertToReturnFormat = (items) => {
        const grouped = {};

        items.forEach((item) => {
            if (!grouped[item.supplierId]) {
                grouped[item.supplierId] = {
                    parentId: item.parentId,
                    parentName: item.parentName,
                    supplierId: item.supplierId,
                    supplierName: item.supplierName,
                    plants: item.plants,
                    rowKey
                };
            }
        });

        return Object.values(grouped);
    };

    /* ------------------ Handlers ------------------ */

    const notifyChange = (nextSelected) => {
        setSelectedItems(nextSelected);
        const grouped = convertToReturnFormat(nextSelected);
        onChangeItems && onChangeItems(name, grouped);
    };

    const handleParentToggle = (parent) => {
        const supplierId = parent.supplierId;
        const children = optionsOfParent(supplierId);
        const parentAlready = isParentSelected(supplierId);
        const exists = selectedItems.some(s => s.supplierId === supplierId);
        let next;
        if (exists) {
            next = selectedItems.filter(s => s.supplierId !== supplierId);
        } else {
            next = [
                ...selectedItems,
                {
                    type: "parent",
                    supplierId,
                    supplierName: parent.supplierName,
                    parentId: parent.parentId,
                    parentName: parent.parentName,
                    plants: parent.plants,
                    rowKey
                }
            ];
        }
        notifyChange(next);
    };

    const handleRemoveParent = (supplierId) => {
        const updated = selectedItems.filter(item => item.supplierId !== supplierId);

        setSelectedItems(updated);
        notifyChange(updated);
    };

    /* ------------------ Filtering ------------------ */

    const filteredFlat = useMemo(() => {
        if (!inputValue) return flattened;

        const q = inputValue.trim().toLowerCase();
        if (!q) return flattened;

        const out = [];
        let i = 0;

        // we include parent if parentId or parentName matches OR any child matches
        while (i < flattened.length) {
            const item = flattened[i];
            if (item.type === "parent") {
                const parentMatch =
                    String(item.supplierId).toLowerCase().includes(q) ||
                    String(item.supplierName || item.title || "").toLowerCase().includes(q);

                // children of this parent
                const children = optionsOfParent(item.supplierId);
                const matchedChildren = children.filter(
                    (c) =>
                        String(c?.supplierId).toLowerCase().includes(q) ||
                        String(c?.supplierName || c.title || "").toLowerCase().includes(q)
                );

                if (parentMatch || matchedChildren.length > 0) {
                    out.push(item);
                    // push matched children (or all children if parent matched)
                    if (parentMatch) {
                        children.forEach((c) => out.push(c));
                    } else {
                        matchedChildren.forEach((c) => out.push(c));
                    }
                }

                // skip to next parent (skip children already processed)
                i++;
                while (i < flattened.length && flattened[i].type === "child") i++;
            } else {
                // Shouldn't happen in our flattening (we always put parent before children)
                i++;
            }
        }

        return out;
    }, [inputValue, flattened]);

    /* ------------------ Autocomplete helpers ------------------ */

    const isOptionEqualToValue = (option, value) => {
        if (!option || !value) return false;
        if (option.type === "parent" && value.type === "parent")
            return option.supplierId === value.supplierId && option.rowKey === value.rowKey;
        if (option.type === "child" && value.type === "child")
            return option.supplierId === value.supplierId;
        // cross-compare (option from list vs value from selectedItems)
        if (option.type === "child" && value.type === "parent")
            return false;
        if (option.type === "parent" && value.type === "child")
            return false;
        return false;
    };

    const getOptionLabel = (option) => {
        if (!option) return "";
        if (option.type === "all") return "Select All";
        if (option.type === "parent") return `${option.supplierId} | ${option.supplierName}`;
        // if (option.type === "child") return `${option.supplierId} | ${option.supplierName || option.title}`;
        return option.title || "";
    };

    /* ------------------ Renderers ------------------ */

    const handleSelectAllToggle = () => {
        const totalOptions = flattened.filter(o => o.type === "parent").length;

        const allSelected = selectedItems.length === totalOptions;

        if (allSelected) {
            notifyChange([]);         // UNSELECT ALL
        } else {
            handleSelectAll();        // SELECT ALL
        }
    };
    const handleSelectAll = () => {
        const allParents = flattened.filter(o => o.type === "parent");
        const next = [
            ...allParents.map(p => ({
                type: "parent",
                parentId: p.parentId,
                parentName: p.parentName,
                supplierId: p.supplierId,
                supplierName: p.supplierName,
                plants: p.plants,
                rowKey
            })),
        ];
        notifyChange(next);
    };

    const renderOption = (props, option) => {
        if (!option) return null;

        // SELECT ALL ROW
        if (option.type === "all") {
            const allChildrenCount = flattened.filter(o => o.type === "parent").length;
            const selectedChildrenCount = selectedItems.filter(s => s.type === "parent").length;
            const checked = selectedChildrenCount === allChildrenCount && allChildrenCount > 0;

            return (
                <li
                    {...props}
                    key="select_all_option"
                    onClick={(e) => {
                        e.preventDefault();
                        handleSelectAllToggle();
                    }}
                    style={{
                        padding: "6px 8px",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 'normal',
                        cursor: "pointer",
                        background: "#f4f4f4"
                    }}
                >
                    <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={checked}
                        style={{ marginRight: 8 }}
                    />
                    <span>Select All</span>
                </li>
            );
        }


        // Parent row with checkbox
        if (option.type === "parent") {
            const supplierId = option.supplierId;
            const groupChildren = optionsOfParent(supplierId);
            const allSelected = groupChildren.length > 0;
            const parentExists = isParentSelected(supplierId);

            const checked = parentExists || allSelected;

            return (
                <li
                    {...props}
                    key={`parent_${supplierId}`}
                    data-type="group-header"
                    onClick={(e) => {
                        e.preventDefault();
                        handleParentToggle(option);
                    }}
                    style={{
                        fontWeight: 'normal',
                        padding: "6px 8px",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        cursor: "pointer"
                    }}
                >
                    <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={checked}
                        style={{ marginRight: 8 }}
                    />
                    <span style={{
                        flex: 1,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                    }}>{`${option.supplierId} | ${option.supplierName}`}</span>
                </li>
            );
        }



        // fallback
        return (
            <li {...props} key={option.id || option.title}>
                {getOptionLabel(option)}
            </li>
        );
    };

    const renderTags = (selected, getTagProps) => {

        const parentCount = selected.filter(s => s.type === "parent").length;

        if (parentCount === 0) return null;

        return (
            <Box onClick={() => setOpenModal(true)} sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <span
                    style={{
                        fontSize: 20,
                        marginRight: 10
                    }}
                >{parentCount}</span>
            </Box>
        );

    };

    /* ------------------ Modal content ------------------ */

    const groupedForModal = convertToReturnFormat(selectedItems);
    function mergeByParentId(data) {
        const grouped = {};

        data.forEach(item => {
            const key = item.parentId;

            if (!grouped[key]) {
                grouped[key] = {
                    parentId: item.parentId,
                    parentName: item.parentName,
                    supplierIds: []
                };
            }

            grouped[key].supplierIds.push({
                supplierId: item.supplierId,
                supplierName: item.supplierName,
                plants: item.plants || []
            });
        });

        return Object.values(grouped);
    }

    const modalDataGroupedByParent = mergeByParentId(groupedForModal);

    const handleRemoveSupplierCorrect = (supplierId) => {
        const next = selectedItems.filter(i => !(i.parentId === supplierId));
        notifyChange(next);
    };
    const handleRemovePlantCorrect = (supplierId) => {
        const next = selectedItems.filter(i => !(i.supplierId === supplierId));
        notifyChange(next);
    };
    return (
        <div>
            <Autocomplete
                id={id || "grouped-multiselect"}
                name={name || "autoComplete_name"}
                multiple
                disableCloseOnSelect
                options={filteredFlat}
                // ListboxComponent={ListboxComponent}
                value={selectedItems}
                onChange={(e, value, reason) => {
                    // We do manual handling via clicks; only handle clear
                    if (reason === "clear") {
                        notifyChange([]);
                    }
                }}
                isOptionEqualToValue={isOptionEqualToValue}
                getOptionLabel={getOptionLabel}
                filterOptions={(opts) => opts}
                inputValue={inputValue}
                onInputChange={(e, val) => setInputValue(val)}
                renderOption={renderOption}
                renderTags={renderTags}
                disablePortal
                freeSolo={false}
                disabled={disabled || false}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search"
                        // disabled={disabled || false}
                        sx={{
                            width,
                            "& .MuiInputBase-root": {
                                height,
                                fontSize,
                                paddingRight: "32px"
                            },
                            "& .MuiInputLabel-root": { fontSize },
                            "& .MuiOutlinedInput-root": { padding: "5px !important" }
                        }}
                        onClick={() => {
                            // sync nothing special; open handled by MUI
                        }}
                    />
                )}
                style={{ maxWidth: width }}
            />

            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
                <DialogTitle>Selected Options</DialogTitle>
                <DialogContent sx={{ maxHeight: 360, overflowY: "auto" }}>
                    {modalDataGroupedByParent.length === 0 ? (
                        <Box sx={{ p: 2 }}>No items selected</Box>
                    ) : (
                        modalDataGroupedByParent.map(grp => (
                            <Box key={grp.supplierId} sx={{ mb: 2, borderBottom: '1px solid #bcbcbc' }}>
                                <Box sx={{ fontWeight: 700, mb: 1 }}>{`${grp.parentId} | ${grp.parentName}`}</Box>
                                {grp?.supplierIds?.map((item) => (
                                    disabled ?
                                        <Chip
                                            key={item.supplierId}
                                            label={`${item.supplierId} | ${item.supplierName}`}
                                            sx={{ m: 0.5 }}
                                        />
                                        : <Chip
                                            key={item.supplierId}
                                            label={`${item.supplierId} | ${item.supplierName}`}
                                            onDelete={() => handleRemovePlantCorrect(item.supplierId)}
                                            sx={{ m: 0.5 }}
                                        />

                                ))}

                                {!disabled && <Box sx={{ mt: 1 }}>
                                    <Button size="small" onClick={() => handleRemoveSupplierCorrect(grp.parentId)}>Remove Child Vendor</Button>
                                </Box>}
                            </Box>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
